import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

export function RotateExtrudeDiagram({ values }: DiagramProps) {
  const angle = Math.min(360, Math.max(1, values.angle ?? 360));
  const fn = Math.max(3, values.fn ?? 30);

  const cx = 110, cy = 75; // center of rotation (Z axis)
  const axisX = cx;  // Z axis position
  const profileX = axisX + 38; // profile offset from Z axis
  const profileW = 16, profileH = 36;
  const profileY = cy - profileH / 2;

  // Sweep arc (top ellipse representing the swept path)
  const outerR = profileX - axisX + profileW; // outer torus radius
  const innerR = profileX - axisX;
  const ry = 12; // ellipse minor axis (perspective)

  const sweepFrac = angle / 360;
  const fullCircle = angle >= 360;

  // Arc path for the outer edge of the torus
  function ellipseArc(rx: number, startDeg: number, endDeg: number): string {
    const s = (startDeg * Math.PI) / 180;
    const e = (endDeg * Math.PI) / 180;
    const x1 = cx + rx * Math.cos(s);
    const y1 = cy + ry * Math.sin(s);
    const x2 = cx + rx * Math.cos(e);
    const y2 = cy + ry * Math.sin(e);
    const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    if (fullCircle) return `M${x1},${y1} A${rx},${ry} 0 1,1 ${x1 - 0.01},${y1} Z`;
    return `M${x1},${y1} A${rx},${ry} 0 ${large},1 ${x2},${y2}`;
  }

  const sweepEndDeg = -90 + angle;

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Rotate extrude diagram">
      <defs>
        <marker id="arr-re" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.Z_COLOR} />
        </marker>
      </defs>

      {/* Z axis */}
      <line x1={axisX} y1={20} x2={axisX} y2={130}
            stroke={D.Z_COLOR} strokeWidth="1.2" strokeDasharray="4 3" opacity="0.6"
            markerEnd="url(#arr-re)" />
      <text x={axisX - 8} y={18} fontSize="9" fontFamily="monospace"
            fill={D.Z_COLOR} opacity="0.8">Z</text>

      {/* Torus body (outer arc) */}
      <path d={ellipseArc(outerR, -90, sweepEndDeg)}
            fill="none" stroke={D.GEO_STROKE} strokeWidth="1.8" opacity="0.9" />
      {/* Inner arc */}
      <path d={ellipseArc(innerR, -90, sweepEndDeg)}
            fill="none" stroke={D.GEO_STROKE} strokeWidth="1.2" opacity="0.5" strokeDasharray="3 3" />

      {/* Gap indicator (if partial) */}
      {!fullCircle && (
        <>
          <path d={ellipseArc(outerR, sweepEndDeg, 270)}
                fill="none" stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 4" opacity="0.25" />
          <text x={cx + outerR + 8} y={cy + ry + 14} fontSize="9" fontFamily="monospace"
                fill={D.Z_COLOR} opacity="0.75">{angle}°</text>
        </>
      )}

      {/* Profile rectangle (the 2D shape being swept) */}
      <rect x={profileX} y={profileY} width={profileW} height={profileH}
            fill="rgba(96,165,250,0.2)" stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <text x={profileX + profileW / 2} y={profileY - 6} fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.55" textAnchor="middle">profile</text>

      {/* Sweep curved arrow */}
      <path d={`M${cx + outerR - 4},${cy - ry - 4} A${outerR},${ry} 0 0,0 ${cx - 8},${cy - ry}`}
            fill="none" stroke="#f59e0b" strokeWidth="1.5" opacity="0.7"
            markerEnd="url(#arr-re)" />

      {/* $fn */}
      <text x="215" y="110" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.4">$fn={fn}</text>
      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.4">profile is rotated around Z axis by angle°</text>
    </svg>
  );
}
